import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const empty = { name: "", contact_person: "", email: "", phone: "", address: "", payment_terms: "", notes: "" };

export default function Suppliers() {
  const { can } = useAuth();
  const confirm = useConfirm();
  const editable = can("suppliers");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/suppliers").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/suppliers/${editing}`, form);
      else await api.post("/suppliers", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (s) => { if (!(await confirm({ title: "Delete supplier?", description: `"${s.name}" will be permanently removed.`, confirmText: "Delete" }))) return; await api.delete(`/suppliers/${s.id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <PageHeader title="Suppliers" subtitle={`${items.length} supplier accounts`}>
        {editable && <Button onClick={() => { setForm(empty); setEditing(null); setOpen(true); }} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="add-supplier-button"><Plus className="h-4 w-4 mr-1.5" /> Add Supplier</Button>}
      </PageHeader>

      <div className="bg-white border border-border rounded-md overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-secondary hover:bg-secondary">
            <TableHead>Supplier</TableHead><TableHead>Contact</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Terms</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.id} data-testid="supplier-row">
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell>{s.contact_person}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{s.email}</TableCell>
                <TableCell className="text-sm">{s.phone}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{s.payment_terms}</TableCell>
                <TableCell className="text-right">
                  {editable && <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setForm({ ...empty, ...s }); setEditing(s.id); setOpen(true); }}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => del(s)} data-testid={`delete-supplier-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No suppliers yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit" : "New"} Supplier</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <F label="Supplier Name" cn="col-span-2"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" /></F>
            <F label="Contact Person"><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></F>
            <F label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></F>
            <F label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></F>
            <F label="Payment Terms"><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="e.g. 30 days" /></F>
            <F label="Address" cn="col-span-2"><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} /></F>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="save-supplier-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children, cn = "" }) {
  return <div className={`space-y-1.5 ${cn}`}><Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>{children}</div>;
}
